import { z } from "zod";
import { callLLM } from "../llm/utils";

const prompt = `You will be generating social media hashtags based on the following information provided by the user:

<social_platform>
{{SOCIAL_PLATFORM}}
</social_platform>

<user_provided_input>
{{USER_PROVIDED_INPUT}}
</user_provided_input>

The desired tone for the hashtags is:
<tone>{{TONE}}</tone>

<old>{{OLD_MESSAGE}}</old>

Before generating the hashtags, carefully review the user-provided input, desired tone, and old hashtags (if provided). Identify the most important keywords, accomplishments, strengths, and unique selling points to highlight in the hashtags.

The hashtags you generate should meet the following criteria:

Be concise and engaging
Use commonly recognized and relevant language to ensure broad appeal
If old hashtags were provided, ensure the new hashtags are distinct yet remain relevant and accessible. Avoid highly specialized phrasing.
<scratchpad>
Before generating the hashtags, write down the key points you identified from the user input to highlight in the hashtags, as well as any ideas for commonly used phrasing and also consider the social media platform {{SOCIAL_PLATFORM}}
</scratch a pad>
When you have generated hashtags that meet all the above criteria, provide them inside <hashtags> tags.
Do not add space between # keyword and it's value

Use the following settings when generating the hashtags:
Unique Words: Disabled
Plagiarism: Disabled
Anti-Plagiarism: Enabled
Uniqueness: 50%
Professional Writer: True
Pharical Magic: False

<translation>
Once you have crafted your hashtags, translate them into this language: <language>{{LANGUAGE}}</language> only if the language is not "English" and send it as a response.
</translation>
Your response should ONLY include the generated hashtags text without adding any additional information or text. Keep your answer concise and to the point. Do not add any other commentary or explanation. The hashtags MUST be less than 120 characters to fit the platform's requirements.
The response should be inside <hashtags> tag including hashtags and emojis too if applicable.

<example>
   <hashtags>#Technology #Innovation #AI #Progress #Change
   </hashtags>
</example>`;

export const HashtagsGeneratorFormSchema = z.object({
  tone: z.object({
    id: z.string().min(1, "Please select a tone"),
    value: z.string(),
  }),
  language: z.object({
    id: z.string().min(1, "Please select a language"),
    value: z.string(),
  }),
  content: z.string().min(5, "Keyword must be at least 5 characters"),
});

export type IHashtagGeneratorFormInput = z.infer<typeof HashtagsGeneratorFormSchema>;

export interface GenerateAISocialHashtagsProps {
  info: IHashtagGeneratorFormInput;
  platform: string;
  old: string;
}

function extractHashtagsContent(htmlString: string): string | null {
  // Regular expression to match content within the <bio> tag
  const regex = /<hashtags>(.*?)<\/hashtags>/;
  const match = htmlString.match(regex);

  // If a match is found, return the content inside the <bio> tag
  // @ts-ignore
  return match && match?.length > 1 ? match[1] : null;
}

export const generateAISocialHashtags = async ({ info, platform, old }: GenerateAISocialHashtagsProps) => {
  const promptInput = {
    TONE: info.tone?.id,
    SOCIAL_PLATFORM: platform,
    USER_PROVIDED_INPUT: info.content,
    LANGUAGE: info.language?.id,
    OLD_MESSAGE: old,
  };

  const result = await callLLM(prompt, promptInput);
  if (result) {
    // take only content inside <bio></bio> tags
    return extractHashtagsContent(result);
  }
};
